import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { cn } from '../cn.js';
import { Skeleton } from './display.js';

export function TableContainer({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('relative overflow-x-auto rounded-lg border border-line bg-bg', className)}>{children}</div>;
}

export function Table({ className, children, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <table className={cn('w-full border-collapse text-sm', className)} {...props}>
      {children}
    </table>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="border-b border-line bg-subtle">{children}</thead>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="[&>tr:last-child]:border-b-0">{children}</tbody>;
}

export function Th({
  className,
  align = 'left',
  sort,
  onSort,
  children,
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & {
  align?: 'left' | 'right' | 'center';
  sort?: 'asc' | 'desc' | false;
  onSort?: () => void;
}) {
  const content = (
    <>
      {children}
      {sort === 'asc' && <ArrowUp className="size-3" aria-hidden="true" />}
      {sort === 'desc' && <ArrowDown className="size-3" aria-hidden="true" />}
    </>
  );
  return (
    <th
      scope="col"
      aria-sort={sort === 'asc' ? 'ascending' : sort === 'desc' ? 'descending' : undefined}
      className={cn(
        'h-8 px-3 text-xs font-medium whitespace-nowrap text-fg-3 first:pl-4 last:pr-4',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
      {...props}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          className={cn('inline-flex items-center gap-1 hover:text-fg', align === 'right' && 'flex-row-reverse')}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </th>
  );
}

export function Tr({
  className,
  onClick,
  selected,
  children,
  ...props
}: HTMLAttributes<HTMLTableRowElement> & { selected?: boolean }) {
  return (
    <tr
      className={cn(
        'border-b border-line transition-colors duration-75',
        onClick && 'cursor-pointer hover:bg-subtle',
        selected && 'bg-brand-subtle/50',
        className,
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </tr>
  );
}

export function Td({
  className,
  align = 'left',
  children,
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'right' | 'center' }) {
  return (
    <td
      className={cn(
        'h-10 px-3 align-middle whitespace-nowrap text-fg first:pl-4 last:pr-4',
        align === 'right' ? 'tabular text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

export function TableSkeleton({ columns, rows = 6 }: { columns: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <Tr key={r}>
          {Array.from({ length: columns }, (_, c) => (
            <Td key={c}>
              <Skeleton className={cn('h-3', c === 0 ? 'w-40' : 'w-16')} />
            </Td>
          ))}
        </Tr>
      ))}
    </>
  );
}

export function TableMessage({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={columns}>{children}</td>
    </tr>
  );
}
